/**
 * Document List Component
 * Displays generated documents with filtering and status management
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  FileText,
  Search,
  Filter,
  ChevronDown,
  Loader,
  AlertCircle,
  Clock,
  User,
  Tag,
  Download,
  Edit2,
  Trash2,
  CheckCircle,
  XCircle,
  Send,
  Eye,
  MoreVertical,
  FileSignature
} from 'lucide-react';
import { Card, CardHeader } from '../../common/Card';
import { Button } from '../../common/Button';
import { Input } from '../../common/Input';
import { useAuth } from '../../../contexts/AuthContext';
import { documentService } from '../../../services/api/documentGeneratorService';
import {
  GeneratedDocument,
  DocumentStatus,
  DocumentCategory,
  DocumentFilters,
  DOCUMENT_STATUS_LABELS,
  DOCUMENT_CATEGORY_LABELS
} from '../../../types/documentGenerator.types';

interface DocumentListProps {
  onViewDocument?: (document: GeneratedDocument) => void;
  onEditDocument?: (document: GeneratedDocument) => void;
}

const statusColors: Record<DocumentStatus, string> = {
  draft: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
  pending_review: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
  pending_approval: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  approved: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  rejected: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
  pending_signature: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
  signed: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300',
  expired: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
  archived: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
};

const statusIcons: Record<DocumentStatus, React.ReactNode> = {
  draft: <FileText className="w-3.5 h-3.5" />,
  pending_review: <Clock className="w-3.5 h-3.5" />,
  pending_approval: <Clock className="w-3.5 h-3.5" />,
  approved: <CheckCircle className="w-3.5 h-3.5" />,
  rejected: <XCircle className="w-3.5 h-3.5" />,
  pending_signature: <FileSignature className="w-3.5 h-3.5" />,
  signed: <CheckCircle className="w-3.5 h-3.5" />,
  expired: <Clock className="w-3.5 h-3.5" />,
  archived: <FileText className="w-3.5 h-3.5" />
};

export const DocumentList: React.FC<DocumentListProps> = ({
  onViewDocument,
  onEditDocument
}) => {
  const { token } = useAuth();
  const [documents, setDocuments] = useState<GeneratedDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<DocumentStatus | ''>('');
  const [categoryFilter, setCategoryFilter] = useState<DocumentCategory | ''>('');
  const [showFilterMenu, setShowFilterMenu] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0
  });

  const fetchDocuments = useCallback(async () => {
    if (!token) return;

    setLoading(true);
    setError(null);

    try {
      const filters: DocumentFilters = {
        page: pagination.page,
        limit: pagination.limit
      };

      if (searchQuery) filters.search = searchQuery;
      if (statusFilter) filters.status = statusFilter;
      if (categoryFilter) filters.category = categoryFilter;

      const result = await documentService.getAll(token, filters);
      setDocuments(result.data);
      setPagination(prev => ({
        ...prev,
        total: result.pagination.total,
        totalPages: result.pagination.totalPages
      }));
    } catch (err: any) {
      setError(err.message || 'Failed to fetch documents');
    } finally {
      setLoading(false);
    }
  }, [token, pagination.page, pagination.limit, searchQuery, statusFilter, categoryFilter]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => setActiveDropdown(null);
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleStatusChange = (status: DocumentStatus | '') => {
    setStatusFilter(status);
    setPagination(prev => ({ ...prev, page: 1 }));
    setShowFilterMenu(false);
  };

  const handleDelete = async (document: GeneratedDocument) => {
    if (!token) return;
    if (!window.confirm(`Are you sure you want to delete "${document.title}"?`)) return;

    try {
      await documentService.delete(token, document._id);
      fetchDocuments();
    } catch (err: any) {
      setError(err.message || 'Failed to delete document');
    }
  };

  const handleDownloadPdf = async (doc: GeneratedDocument) => {
    if (!token) return;

    try {
      const blob = await documentService.downloadPdf(token, doc._id);
      const url = URL.createObjectURL(blob);
      const a = window.document.createElement('a');
      a.href = url;
      a.download = `${doc.title}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err: any) {
      setError(err.message || 'Failed to download PDF');
    }
  };

  const handleDownloadDocx = async (doc: GeneratedDocument) => {
    if (!token) return;

    try {
      const blob = await documentService.downloadDocx(token, doc._id);
      const url = URL.createObjectURL(blob);
      const a = window.document.createElement('a');
      a.href = url;
      a.download = `${doc.title}.docx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err: any) {
      setError(err.message || 'Failed to download DOCX');
    }
  };

  const handleSubmitForApproval = async (doc: GeneratedDocument) => {
    if (!token) return;

    try {
      await documentService.submitForApproval(token, doc._id);
      fetchDocuments();
    } catch (err: any) {
      setError(err.message || 'Failed to submit for approval');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-semibold text-text-primary">Generated Documents</h2>
          <p className="text-text-secondary mt-1">
            {pagination.total} document{pagination.total !== 1 ? 's' : ''} found
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-text-tertiary" />
          <Input
            type="text"
            placeholder="Search documents..."
            value={searchQuery}
            onChange={handleSearch}
            className="pl-10"
          />
        </div>

        <div className="relative">
          <Button
            variant="secondary"
            onClick={() => setShowFilterMenu(!showFilterMenu)}
            className="gap-2"
          >
            <Filter className="w-4 h-4" />
            {statusFilter ? DOCUMENT_STATUS_LABELS[statusFilter] : 'All Statuses'}
            <ChevronDown className="w-4 h-4" />
          </Button>

          {showFilterMenu && (
            <div className="absolute right-0 mt-2 w-56 bg-surface-base rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-1 z-50">
              <button
                onClick={() => handleStatusChange('')}
                className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-800 ${
                  !statusFilter ? 'bg-gray-50 dark:bg-gray-800 font-medium' : ''
                }`}
              >
                All Statuses
              </button>
              {(Object.keys(DOCUMENT_STATUS_LABELS) as DocumentStatus[]).map(status => (
                <button
                  key={status}
                  onClick={() => handleStatusChange(status)}
                  className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-800 flex items-center gap-2 ${
                    statusFilter === status ? 'bg-gray-50 dark:bg-gray-800 font-medium' : ''
                  }`}
                >
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs ${statusColors[status]}`}>
                    {statusIcons[status]}
                  </span>
                  {DOCUMENT_STATUS_LABELS[status]}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="flex items-center gap-2 p-4 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-lg">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <p>{error}</p>
        </div>
      )}

      {/* Loading State */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader className="w-8 h-8 animate-spin text-primary-600" />
        </div>
      ) : documents.length === 0 ? (
        <Card className="text-center py-12">
          <FileText className="w-12 h-12 mx-auto text-text-tertiary mb-4" />
          <h3 className="text-lg font-medium text-text-primary">No documents found</h3>
          <p className="text-text-secondary mt-1">
            {searchQuery || statusFilter
              ? 'Try adjusting your search or filters'
              : 'Generate your first document from a template'}
          </p>
        </Card>
      ) : (
        /* Document Table */
        <Card padding="none">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-800">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                    Document
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                    Category
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                    Created
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                    Created By
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-text-secondary uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {documents.map(doc => (
                  <tr
                    key={doc._id}
                    className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
                  >
                    <td className="px-6 py-4">
                      <div
                        className="flex items-center gap-3 cursor-pointer"
                        onClick={() => onViewDocument?.(doc)}
                      >
                        <div className="p-2 rounded-lg bg-primary-50 dark:bg-primary-900/20">
                          <FileText className="w-5 h-5 text-primary-600" />
                        </div>
                        <div>
                          <p className="font-medium text-text-primary hover:text-primary-600 transition-colors">
                            {doc.title}
                          </p>
                          <p className="text-xs text-text-tertiary">{doc.documentNumber}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm text-text-secondary">
                        {DOCUMENT_CATEGORY_LABELS[doc.category]}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full ${statusColors[doc.status]}`}>
                        {statusIcons[doc.status]}
                        {DOCUMENT_STATUS_LABELS[doc.status]}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-1 text-sm text-text-secondary">
                        <Clock className="w-4 h-4" />
                        {formatDate(doc.createdAt)}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-1 text-sm text-text-secondary">
                        <User className="w-4 h-4" />
                        {doc.createdBy.firstName} {doc.createdBy.lastName}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="relative inline-block">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setActiveDropdown(activeDropdown === doc._id ? null : doc._id);
                          }}
                          className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                        >
                          <MoreVertical className="w-5 h-5 text-text-secondary" />
                        </button>

                        {activeDropdown === doc._id && (
                          <div className="absolute right-0 mt-1 w-48 bg-surface-base rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-1 z-50">
                            <button
                              onClick={() => {
                                onViewDocument?.(doc);
                                setActiveDropdown(null);
                              }}
                              className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-800 flex items-center gap-2"
                            >
                              <Eye className="w-4 h-4" />
                              View
                            </button>
                            {doc.status === 'draft' && (
                              <>
                                <button
                                  onClick={() => {
                                    onEditDocument?.(doc);
                                    setActiveDropdown(null);
                                  }}
                                  className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-800 flex items-center gap-2"
                                >
                                  <Edit2 className="w-4 h-4" />
                                  Edit
                                </button>
                                <button
                                  onClick={() => {
                                    handleSubmitForApproval(doc);
                                    setActiveDropdown(null);
                                  }}
                                  className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-800 flex items-center gap-2"
                                >
                                  <Send className="w-4 h-4" />
                                  Submit for Approval
                                </button>
                              </>
                            )}
                            <hr className="my-1 border-gray-200 dark:border-gray-700" />
                            <button
                              onClick={() => {
                                handleDownloadPdf(doc);
                                setActiveDropdown(null);
                              }}
                              className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-800 flex items-center gap-2"
                            >
                              <Download className="w-4 h-4" />
                              Download PDF
                            </button>
                            <button
                              onClick={() => {
                                handleDownloadDocx(doc);
                                setActiveDropdown(null);
                              }}
                              className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-800 flex items-center gap-2"
                            >
                              <Download className="w-4 h-4" />
                              Download DOCX
                            </button>
                            <hr className="my-1 border-gray-200 dark:border-gray-700" />
                            <button
                              onClick={() => {
                                handleDelete(doc);
                                setActiveDropdown(null);
                              }}
                              className="w-full px-4 py-2 text-left text-sm hover:bg-red-50 dark:hover:bg-red-900/20 text-red-600 flex items-center gap-2"
                            >
                              <Trash2 className="w-4 h-4" />
                              Delete
                            </button>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <Button
            variant="secondary"
            size="sm"
            disabled={pagination.page === 1}
            onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
          >
            Previous
          </Button>
          <span className="text-sm text-text-secondary px-4">
            Page {pagination.page} of {pagination.totalPages}
          </span>
          <Button
            variant="secondary"
            size="sm"
            disabled={pagination.page === pagination.totalPages}
            onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
          >
            Next
          </Button>
        </div>
      )}
    </div>
  );
};

export default DocumentList;
